// Schreibzugriffe des Zählerbereichs. Ablesung löschen/nachtragen hält den gespeicherten Verbrauch der
// folgenden Ablesung stimmig; das Beleg-Foto wird beim Löschen aus dem Bucket entfernt.

import { supabase } from '../supabase/client';
import { korrekturNachEinfuegen, korrekturNachLoeschen, fotoEndung, fotoPfad, type AblesungKern, type VerbrauchKorrektur } from './ablesungen';

export const FOTO_BUCKET = 'zaehler-fotos';

export interface AblesungMitFoto extends AblesungKern { foto_url: string | null }

async function schreibeKorrektur(k: VerbrauchKorrektur | null): Promise<string | null> {
  if (!k) return null;
  const { error } = await supabase.from('ablesungen').update({ verbrauch: k.verbrauch }).eq('id', k.id);
  return error ? error.message : null;
}

export interface LoeschErgebnis {
  /** null = kein Foto; false = Datei blieb im Bucket (Storage-Regel fehlt oder Fehler) */
  fotoEntfernt: boolean | null;
  /** Fehlertext, falls der Verbrauch der Folge-Ablesung nicht angepasst werden konnte */
  korrekturFehler: string | null;
}

/** Löscht die Ablesung, passt den Verbrauch der folgenden an und entfernt das Foto (falls sonst unbenutzt). */
export async function loescheAblesung(ablesung: AblesungMitFoto, alle: readonly AblesungMitFoto[]): Promise<LoeschErgebnis> {
  const { data, error } = await supabase.from('ablesungen').delete().eq('id', ablesung.id).select('id');
  if (error) throw new Error(error.message);
  if (!data || data.length === 0) throw new Error('Ablesung konnte nicht gelöscht werden.');

  const korrekturFehler = await schreibeKorrektur(korrekturNachLoeschen(alle, ablesung.id));

  const pfad = fotoPfad(ablesung.foto_url, FOTO_BUCKET);
  let fotoEntfernt: boolean | null = null;
  if (pfad && !alle.some(a => a.id !== ablesung.id && a.foto_url === ablesung.foto_url)) {
    // remove() meldet ohne Lese-Regel keinen Fehler, löscht aber nichts – darum die Rückgabe prüfen
    const { data: weg, error: fotoFehler } = await supabase.storage.from(FOTO_BUCKET).remove([pfad]);
    fotoEntfernt = !fotoFehler && (weg?.length ?? 0) > 0;
  }
  return { fotoEntfernt, korrekturFehler };
}

/**
 * Passt nach dem Speichern einer Ablesung den Verbrauch der direkt folgenden an (Nachtrag zwischen zwei Ablesungen).
 * `alle` enthält die neue Ablesung bereits. Liefert einen Fehlertext oder null.
 */
export async function korrigiereNachEinfuegen(alle: readonly AblesungKern[], neuId: string): Promise<string | null> {
  return schreibeKorrektur(korrekturNachEinfuegen(alle, neuId));
}

/** Aktiv/Inaktiv umschalten (wie bisher mit updated_at). */
export async function setzeAktiv(id: string, aktiv: boolean): Promise<void> {
  const { error } = await supabase.from('zaehler').update({ aktiv, updated_at: new Date().toISOString() }).eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * Zähler löschen (Ablesungen löschen sich per ON DELETE CASCADE mit) und danach deren Beleg-Fotos aus dem Bucket
 * entfernen. Liefert, ob alle Fotos weg sind (null = es gab keine).
 */
export async function loescheZaehler(id: string, fotoUrls: readonly (string | null)[]): Promise<{ fotosEntfernt: boolean | null }> {
  const { data, error } = await supabase.from('zaehler').delete().eq('id', id).select('id');
  if (error) throw new Error(error.message);
  if (!data || data.length === 0) throw new Error('Zähler konnte nicht gelöscht werden.');
  const pfade = [...new Set(fotoUrls.map(u => fotoPfad(u, FOTO_BUCKET)).filter((p): p is string => !!p))];
  if (pfade.length === 0) return { fotosEntfernt: null };
  const { data: weg, error: fotoFehler } = await supabase.storage.from(FOTO_BUCKET).remove(pfade);
  return { fotosEntfernt: !fotoFehler && (weg?.length ?? 0) === pfade.length };
}

/**
 * #512: Foto an eine vorhandene Ablesung hängen bzw. ersetzen. Lädt zuerst hoch, setzt dann foto_url;
 * ein ersetztes Foto wird danach aus dem Bucket entfernt (wenn keine andere Ablesung es nutzt).
 */
export async function setzeAblesungFoto(zaehlerId: string, ablesung: AblesungMitFoto, datei: File, alle: readonly AblesungMitFoto[]): Promise<{ altesEntfernt: boolean | null }> {
  if (!datei.type.startsWith('image/')) throw new Error('Bitte ein Bild auswählen.');
  const pfad = `${zaehlerId}/${Date.now()}.${fotoEndung(datei.name)}`;
  const up = await supabase.storage.from(FOTO_BUCKET).upload(pfad, datei, { contentType: datei.type });
  if (up.error) throw new Error(`Foto konnte nicht hochgeladen werden (${up.error.message}).`);
  const url = supabase.storage.from(FOTO_BUCKET).getPublicUrl(pfad).data.publicUrl;
  const { data, error } = await supabase.from('ablesungen').update({ foto_url: url }).eq('id', ablesung.id).select('id');
  if (error || !data?.length) {
    await supabase.storage.from(FOTO_BUCKET).remove([pfad]);
    throw new Error(error?.message ?? 'Foto konnte nicht gespeichert werden.');
  }
  return { altesEntfernt: await entferneWennUnbenutzt(ablesung, alle) };
}

/** #512: Foto von einer Ablesung entfernen (Ablesung bleibt). */
export async function entferneAblesungFoto(ablesung: AblesungMitFoto, alle: readonly AblesungMitFoto[]): Promise<{ dateiEntfernt: boolean | null }> {
  const { data, error } = await supabase.from('ablesungen').update({ foto_url: null }).eq('id', ablesung.id).select('id');
  if (error || !data?.length) throw new Error(error?.message ?? 'Foto konnte nicht entfernt werden.');
  return { dateiEntfernt: await entferneWennUnbenutzt(ablesung, alle) };
}

async function entferneWennUnbenutzt(ablesung: AblesungMitFoto, alle: readonly AblesungMitFoto[]): Promise<boolean | null> {
  const pfad = fotoPfad(ablesung.foto_url, FOTO_BUCKET);
  if (!pfad || alle.some(a => a.id !== ablesung.id && a.foto_url === ablesung.foto_url)) return null;
  const { data: weg, error } = await supabase.storage.from(FOTO_BUCKET).remove([pfad]);
  return !error && (weg?.length ?? 0) > 0;
}
